import { useMemo, useState } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from 'react-simple-maps';
import { useRankingsNetworkSummaryQuery } from '../api/queries';
import type { RankingsMapPoint } from '../types';

// us-atlas states-10m, served from /public so it bundles with the app.
const GEO_URL = '/us-states-10m.json';

type Props = {
  onDrillIn: (afNumber: string) => void;
  /** When set, only show dots for franchisees assigned to this FBC. */
  fbcFilter?: string | null;
  title?: string;
};

// packCount → fill color. Any presence in the 3-pack = blue
// (darker the better); zero presence = red.
function colorFor(packCount: number): string {
  switch (packCount) {
    case 3:
      return '#1e3a8a';
    case 2:
      return '#2563eb';
    case 1:
      return '#93c5fd';
    case 0:
    default:
      return '#dc2626';
  }
}

function pointKey(p: RankingsMapPoint, jitter: number): { x: number; y: number } {
  // Sub-pixel jitter so co-located AFs (shared office, same zip) don't
  // collapse into a single dot. Deterministic from afNumber so the
  // dot doesn't move between renders.
  const hash = [...p.afNumber].reduce((a, c) => a + c.charCodeAt(0), 0);
  const dx = ((hash % 17) - 8) / 17;
  const dy = (((hash * 31) % 17) - 8) / 17;
  return { x: p.lng + dx * jitter, y: p.lat + dy * jitter };
}

export default function NetworkRankingsMap({
  onDrillIn,
  fbcFilter,
  title = 'Maps 3-Pack Coverage — Network Map',
}: Props) {
  const { data, isLoading, error } = useRankingsNetworkSummaryQuery(true);
  const [hover, setHover] = useState<{ p: RankingsMapPoint; x: number; y: number } | null>(
    null,
  );

  const points = useMemo(() => {
    const all = data?.mapPoints ?? [];
    return fbcFilter ? all.filter((p) => p.fbcName === fbcFilter) : all;
  }, [data?.mapPoints, fbcFilter]);

  const buckets = useMemo(() => {
    const b = { 3: 0, 2: 0, 1: 0, 0: 0 };
    for (const p of points) b[p.packCount as 0 | 1 | 2 | 3]++;
    return b;
  }, [points]);

  if (isLoading) {
    return (
      <>
        <div className="ds-section-header">
          <h2>{title}</h2>
          <span className="meta">Loading…</span>
        </div>
      </>
    );
  }
  if (error) {
    return (
      <>
        <div className="ds-section-header">
          <h2>{title}</h2>
          <span className="meta" style={{ color: '#dc2626' }}>
            Failed to load: {(error as Error).message}
          </span>
        </div>
      </>
    );
  }
  if (points.length === 0) return null;

  return (
    <>
      <div className="ds-section-header">
        <h2>Maps 3-Pack Coverage — Network Map</h2>
        <span className="meta">
          {points.length} locations · color = keywords in Maps 3-pack (0–3) · click a dot to drill in
        </span>
      </div>
      <div
        className="ds-panel"
        style={{ padding: 16, position: 'relative' }}
        onMouseLeave={() => setHover(null)}
      >
        <ComposableMap
          projection="geoAlbersUsa"
          projectionConfig={{ scale: 1000 }}
          width={975}
          height={610}
          style={{ width: '100%', height: 'auto', display: 'block' }}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill="#f1f5f9"
                  stroke="#cbd5e1"
                  strokeWidth={0.5}
                  style={{
                    default: { outline: 'none' },
                    hover: { fill: '#e2e8f0', outline: 'none' },
                    pressed: { outline: 'none' },
                  }}
                />
              ))
            }
          </Geographies>
          {points.map((p) => {
            const jittered = pointKey(p, 0.35);
            return (
              <Marker
                key={p.afNumber}
                coordinates={[jittered.x, jittered.y]}
                onClick={() => onDrillIn(p.afNumber)}
                onMouseEnter={(e) => {
                  const rect = (
                    e.currentTarget.ownerSVGElement!.parentElement as HTMLElement
                  ).getBoundingClientRect();
                  setHover({
                    p,
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top,
                  });
                }}
                onMouseMove={(e) => {
                  const rect = (
                    e.currentTarget.ownerSVGElement!.parentElement as HTMLElement
                  ).getBoundingClientRect();
                  setHover((h) =>
                    h ? { ...h, x: e.clientX - rect.left, y: e.clientY - rect.top } : h,
                  );
                }}
                style={{ default: { cursor: 'pointer' } }}
              >
                <circle
                  r={4.5}
                  fill={colorFor(p.packCount)}
                  fillOpacity={0.85}
                  stroke="#fff"
                  strokeWidth={0.8}
                />
              </Marker>
            );
          })}
        </ComposableMap>

        {hover && (
          <div
            style={{
              position: 'absolute',
              left: Math.min(hover.x + 12, 600),
              top: Math.max(hover.y - 8, 0),
              pointerEvents: 'none',
              background: '#0f172a',
              color: '#fff',
              padding: '8px 10px',
              borderRadius: 6,
              fontSize: 12,
              lineHeight: 1.4,
              boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
              whiteSpace: 'nowrap',
              zIndex: 10,
            }}
          >
            <div style={{ fontWeight: 600 }}>{hover.p.name}</div>
            <div style={{ opacity: 0.8 }}>
              {hover.p.afNumber}
              {hover.p.location ? ` · ${hover.p.location}` : ''}
            </div>
            <div style={{ marginTop: 4 }}>
              3-pack: <strong>{hover.p.packCount}/3</strong> keywords
              {hover.p.avgMapsRank != null && (
                <> · avg pos {hover.p.avgMapsRank.toFixed(1)}</>
              )}
            </div>
          </div>
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            justifyContent: 'center',
            marginTop: 12,
            fontSize: 12,
            color: '#475569',
            flexWrap: 'wrap',
          }}
        >
          <LegendDot color={colorFor(3)} label={`3/3 in pack (${buckets[3]})`} />
          <LegendDot color={colorFor(2)} label={`2/3 (${buckets[2]})`} />
          <LegendDot color={colorFor(1)} label={`1/3 (${buckets[1]})`} />
          <LegendDot color={colorFor(0)} label={`0/3 (${buckets[0]})`} />
        </div>
      </div>
    </>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span
        style={{
          display: 'inline-block',
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: color,
          border: '1px solid #fff',
          boxShadow: '0 0 0 1px #cbd5e1',
        }}
      />
      {label}
    </span>
  );
}
